import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  bulkInvoicesApi,
  BulkInvoiceMeta,
  BulkInvoicePreview,
  BulkInvoiceSelection,
} from '../services/bulkInvoices';

type Step = 1 | 2 | 3 | 4;

interface BulkGenerateInvoicesDialogProps {
  open: boolean;
  onClose: () => void;
  onCompleted: () => void;
}

function toggleInSet<T>(set: Set<T>, value: T, on: boolean): Set<T> {
  const n = new Set(set);
  if (on) n.add(value);
  else n.delete(value);
  return n;
}

export const BulkGenerateInvoicesDialog: React.FC<BulkGenerateInvoicesDialogProps> = ({
  open,
  onClose,
  onCompleted,
}) => {
  const [step, setStep] = useState<Step>(1);
  const [meta, setMeta] = useState<BulkInvoiceMeta | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');

  const [companyIds, setCompanyIds] = useState<Set<number>>(new Set());
  const [serviceIds, setServiceIds] = useState<Set<number>>(new Set());
  const [periodValues, setPeriodValues] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<BulkInvoicePreview | null>(null);
  const [sendEmail, setSendEmail] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setError('');
    setLoadError('');
    setPreview(null);
    setSendEmail(false);
    setLoading(true);
    bulkInvoicesApi
      .getMeta()
      .then((m) => {
        setMeta(m);
        setCompanyIds(new Set(m.companies.map((c) => c.id)));
        setServiceIds(new Set(m.default_services.map((s) => s.id)));
        setPeriodValues(new Set(m.payment_periods.map((p) => p.value)));
      })
      .catch((e) => {
        setLoadError(
          e?.response?.data?.detail
            ? String(e.response.data.detail)
            : 'Failed to load options'
        );
      })
      .finally(() => setLoading(false));
  }, [open]);

  const selectionPayload = (): BulkInvoiceSelection | null => {
    if (companyIds.size === 0 || serviceIds.size === 0 || periodValues.size === 0) {
      return null;
    }
    return {
      company_ids: Array.from(companyIds),
      service_ids: Array.from(serviceIds),
      rel_type_values: Array.from(periodValues),
      send_email: sendEmail,
    };
  };

  const handleNext = async (from: Step) => {
    setError('');
    if (from === 1) {
      if (companyIds.size === 0) {
        setError('Select at least one company.');
        return;
      }
      setStep(2);
      return;
    }
    if (from === 2) {
      if (serviceIds.size === 0) {
        setError('Select at least one service.');
        return;
      }
      setStep(3);
      return;
    }
    if (from === 3) {
      if (periodValues.size === 0) {
        setError('Select at least one payment period.');
        return;
      }
      const body = selectionPayload();
      if (!body) {
        setError('Invalid selection.');
        return;
      }
      setActionLoading(true);
      try {
        const p = await bulkInvoicesApi.preview(body);
        setPreview(p);
        setStep(4);
      } catch (e: any) {
        setError(
          e?.response?.data?.detail
            ? String(e.response.data.detail)
            : 'Preview failed'
        );
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleBack = () => {
    setError('');
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
    else if (step === 4) setStep(3);
  };

  const handleConfirm = async () => {
    const body = selectionPayload();
    if (!body) return;
    setActionLoading(true);
    setError('');
    try {
      const r = await bulkInvoicesApi.execute(body);
      onCompleted();
      onClose();
      setStep(1);
      setPreview(null);
      if (r.errors?.length) {
        window.alert(
          `Created ${r.created}, failed ${r.failed}.\n\n${r.errors.slice(0, 12).join('\n')}`
        );
      }
    } catch (e: any) {
      setError(
        e?.response?.data?.detail
          ? String(e.response.data.detail)
          : 'Something went wrong'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = () => {
    if (actionLoading) return;
    onClose();
    setStep(1);
    setError('');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk generate invoices</DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={32} />
          </Box>
        )}
        {loadError && <Alert severity="error">{loadError}</Alert>}

        {!loading && !loadError && meta && (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {step === 1 && (
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Step 1 of 4 — Select companies
                </Typography>
                <Box display="flex" gap={1} mb={1}>
                  <Button
                    size="small"
                    onClick={() =>
                      setCompanyIds(new Set(meta.companies.map((c) => c.id)))
                    }
                  >
                    Select all
                  </Button>
                  <Button size="small" onClick={() => setCompanyIds(new Set())}>
                    Clear all
                  </Button>
                </Box>
                <Box sx={{ maxHeight: 320, overflow: 'auto', border: 1, borderColor: 'divider', p: 1, borderRadius: 1 }}>
                  <FormGroup>
                    {meta.companies.map((c) => (
                      <FormControlLabel
                        key={c.id}
                        control={
                          <Checkbox
                            checked={companyIds.has(c.id)}
                            onChange={(_, on) =>
                              setCompanyIds((s) => toggleInSet(s, c.id, on))
                            }
                          />
                        }
                        label={c.name}
                      />
                    ))}
                  </FormGroup>
                </Box>
              </Box>
            )}

            {step === 2 && (
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Step 2 of 4 — Default services
                </Typography>
                <Box display="flex" gap={1} mb={1}>
                  <Button
                    size="small"
                    onClick={() =>
                      setServiceIds(new Set(meta.default_services.map((s) => s.id)))
                    }
                  >
                    Select all
                  </Button>
                  <Button size="small" onClick={() => setServiceIds(new Set())}>
                    Clear all
                  </Button>
                </Box>
                <Box sx={{ maxHeight: 320, overflow: 'auto', border: 1, borderColor: 'divider', p: 1, borderRadius: 1 }}>
                  <FormGroup>
                    {meta.default_services.map((s) => (
                      <FormControlLabel
                        key={s.id}
                        control={
                          <Checkbox
                            checked={serviceIds.has(s.id)}
                            onChange={(_, on) =>
                              setServiceIds((x) => toggleInSet(x, s.id, on))
                            }
                          />
                        }
                        label={s.name}
                      />
                    ))}
                  </FormGroup>
                </Box>
              </Box>
            )}

            {step === 3 && (
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Step 3 of 4 — Payment periods
                </Typography>
                <Box display="flex" gap={1} mb={1}>
                  <Button
                    size="small"
                    onClick={() =>
                      setPeriodValues(
                        new Set(meta.payment_periods.map((p) => p.value))
                      )
                    }
                  >
                    Select all
                  </Button>
                  <Button size="small" onClick={() => setPeriodValues(new Set())}>
                    Clear all
                  </Button>
                </Box>
                <Box sx={{ maxHeight: 320, overflow: 'auto', border: 1, borderColor: 'divider', p: 1, borderRadius: 1 }}>
                  <FormGroup>
                    {meta.payment_periods.map((p) => (
                      <FormControlLabel
                        key={p.value}
                        control={
                          <Checkbox
                            checked={periodValues.has(p.value)}
                            onChange={(_, on) =>
                              setPeriodValues((s) => toggleInSet(s, p.value, on))
                            }
                          />
                        }
                        label={p.label}
                      />
                    ))}
                  </FormGroup>
                </Box>
              </Box>
            )}

            {step === 4 && meta && preview && (
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Step 4 of 4 — Review and confirm
                </Typography>
                <Typography variant="body2" paragraph>
                  Companies:{' '}
                  <strong>
                    {meta.companies
                      .filter((c) => companyIds.has(c.id))
                      .map((c) => c.name)
                      .join(', ')}
                  </strong>
                </Typography>
                <Typography variant="body2" paragraph>
                  Services:{' '}
                  <strong>
                    {meta.default_services
                      .filter((s) => serviceIds.has(s.id))
                      .map((s) => s.name)
                      .join(', ')}
                  </strong>
                </Typography>
                <Typography variant="body2" paragraph>
                  Payment periods:{' '}
                  <strong>
                    {meta.payment_periods
                      .filter((p) => periodValues.has(p.value))
                      .map((p) => p.label)
                      .join(', ')}
                  </strong>
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography>
                  Invoices to create: <strong>{preview.transaction_count}</strong> (active
                  agreements with matching lines)
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Companies with at least one invoice: {preview.company_count} /{' '}
                  {companyIds.size} selected
                </Typography>
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Checkbox
                      checked={sendEmail}
                      onChange={(_, c) => setSendEmail(c)}
                    />
                  }
                  label="Send email"
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={actionLoading}>
          Cancel
        </Button>
        {step > 1 && step < 4 && (
          <Button onClick={handleBack} disabled={actionLoading}>
            Back
          </Button>
        )}
        {step < 4 && (
          <Button
            variant="contained"
            onClick={() => void handleNext(step)}
            disabled={actionLoading}
          >
            {actionLoading ? '…' : 'Next'}
          </Button>
        )}
        {step === 4 && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => void handleConfirm()}
            disabled={actionLoading || !preview || (preview?.transaction_count ?? 0) === 0}
          >
            {actionLoading ? '…' : 'Confirm'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
